/**
 * resourceDatasource.ts — grid datasources for the React resource pages.
 *
 * Browse/Collection (`FindAll`) and Search (ES) return very different envelopes,
 * but every datasource maps its rows to one `NormalizedRow` (types/resource.ts) so
 * the grid columns and the resource viewer never branch on the source. This is the
 * React analogue of the per-controller `getRows` mappers in the Angular grids
 * (CollectionController/BrowseController pass FindAll rows through; SearchController
 * remaps ES `hits.fields.*`). Here Browse rows are normalized too, so the canonical
 * field names (`custodian`, numeric `bytes`/`numPdfPages`) hold for both sources.
 *
 * These are thin imperative wrappers around api.ts feeding ag-Grid's IDatasource —
 * NOT TanStack queries — so the usual "hooks wrap useQuery" rule doesn't apply.
 */

import { api, buildQueryString } from './api';
import { broadcastSearchResult } from './angular-bridge';
import type { NormalizedRow } from '../types/resource';

/** ag-Grid column-filter search expression; empty = no column filter. */
export interface SearchExpression {
  operator: number;
  fields: unknown[];
  searchExpressions: unknown[];
}

export const EMPTY_EXPRESSION: SearchExpression = { operator: 0, fields: [], searchExpressions: [] };

export interface PageRequest {
  /** 0-based page index (startRow / limit). Search datasource converts to 1-based. */
  page0: number;
  limit: number;
  sortBy: string | null;
  sortDirection: string | null;
  columnFilters?: SearchExpression;
}

export interface ResourcePage {
  rows: NormalizedRow[];
  totalRows: number;
  totalFileSize: number;
  totalPageCount: number;
}

export interface ResourceDatasource {
  fetchPage(req: PageRequest): Promise<ResourcePage>;
}

// ES returns fileSize/pageCount as strings; FindAll returns numbers. Coerce to number|null.
function toNum(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Email recipient fields come back as string arrays; join for single-cell column display. */
function joinAddrs(v: unknown): string | null {
  if (Array.isArray(v)) return v.length ? v.join('; ') : null;
  return (v as string) ?? null;
}

/** Map a FindAll (Browse/Collection) resource row to the canonical shape. */
export function normalizeBrowseRow(r: any): NormalizedRow {
  return {
    id: r.id,
    projectId: r.projectId,
    collectionId: r.collectionId,
    title: r.title ?? null,
    filename: r.filename ?? null,
    type: r.type ?? null,
    hasVirus: r.hasVirus ?? null,
    category: r.category ?? null,
    extension: r.extension ?? null,
    bytes: toNum(r.bytes),
    numPdfPages: toNum(r.numPdfPages),
    // FindAll calls it custodianName; canonical name is custodian (matches Search).
    custodian: r.custodian ?? r.custodianName ?? null,
    collectionName: r.collectionName ?? null,
    numberOfAncestors: r.numberOfAncestors ?? null,
    path: r.path ?? null,
    folders: r.folders ?? null,
    mimeType: r.mimeType ?? null,
    md5: r.md5 ?? null,
    createdDate: r.createdDate ?? null,
    modifiedDate: r.modifiedDate ?? null,
    creator: r.creator ?? null,
    // FindAll emailFrom is a string|null; emailTo/Cc/Bcc are arrays — join for column display.
    emailFrom: r.emailFrom ?? null,
    emailTo: joinAddrs(r.emailTo),
    emailCc: joinAddrs(r.emailCc),
    emailBcc: joinAddrs(r.emailBcc),
    emailConversationTopic: r.emailConversationTopic ?? null,
    directPdfThumbnailPath: r.directPdfThumbnailPath ?? null,
    lastBegBates: r.lastBegBates ?? null,
    lastEndBates: r.lastEndBates ?? null,
    lastBegAttachBates: r.lastBegAttachBates ?? null,
    lastEndAttachBates: r.lastEndAttachBates ?? null,
    isContainer: r.isContainer ?? null,
    isSourceStorage: r.isSourceStorage ?? null,
    isTopParent: r.isTopParent ?? null,
    hasNotes: r.hasNotes ?? null,
    hasPrivilegedNotes: r.hasPrivilegedNotes ?? null,
    hadProcessingException: r.hadProcessingException ?? null,
    chatConversationId: r.chatConversationId ?? null,
    chatMessageId: r.chatMessageId ?? null,
    chatMessage: r.chatMessage ?? null,
    sortKey: r.sortKey ?? null,
  };
}

interface BrowseDatasourceOptions {
  projectId: string;
  /** Single id (Collection page) or many (Browse page) — joined comma-separated. */
  collectionIds: string | string[];
  /** JSON tag-filter string applied on the page (empty = no tag filter). */
  tagFilter?: string;
}

/**
 * Datasource for Browse + Collection pages. Both hit
 * `POST /project/:pid/collection/:cids/resource` (FindAll) — the only difference is
 * one vs many collection ids in the path. Envelope: { resources, totalResources,
 * totalPageCount, totalFileSize }, page is 0-based. Mirrors CollectionController.getRows.
 */
export function createBrowseDatasource(opts: BrowseDatasourceOptions): ResourceDatasource {
  const cids = Array.isArray(opts.collectionIds) ? opts.collectionIds.join(',') : opts.collectionIds;
  return {
    async fetchPage(req) {
      const qs = buildQueryString({
        page: req.page0,
        limit: req.limit,
        tagFilter: opts.tagFilter ?? '',
        sortBy: req.sortBy,
        sortDirection: req.sortDirection,
      });

      const path = `/project/${opts.projectId}/collection/${cids}/resource?${qs}`;
      const page = await api.post<any>(path, req.columnFilters ?? EMPTY_EXPRESSION);

      return {
        rows: (page.resources ?? []).map(normalizeBrowseRow),
        totalRows: page.totalResources ?? 0,
        totalFileSize: page.totalFileSize ?? 0,
        totalPageCount: page.totalPageCount ?? 0,
      };
    },
  };
}

/**
 * Map one Elasticsearch hit to the canonical row. Search returns a different
 * envelope and field names than FindAll (fields nested under `.fields`, `fileName`
 * vs `filename`, string numerics). Ports SearchController.getRows (:222-265)
 * so search rows match browse rows exactly.
 */
export function normalizeSearchHit(hit: any): NormalizedRow {
  const f = hit.fields ?? {};
  return {
    id: f.resourceId,
    projectId: f.projectId,
    collectionId: f.collectionId,
    title: f.title ?? null,
    filename: f.fileName ?? null,
    type: f.type ?? null,
    hasVirus: f.hasVirus ?? null,
    category: f.category ?? null,
    extension: f.fileExtension ?? null,
    bytes: toNum(f.fileSize),
    numPdfPages: toNum(f.pageCount),
    custodian: f.custodian ?? null,
    // ES hits carry collectionId but not the name; no ancestor count either.
    collectionName: f.collectionName ?? null,
    numberOfAncestors: null,
    path: f.path ?? null,
    folders: f.folders ?? null,
    mimeType: f.mimeType ?? null,
    md5: f.md5 ?? null,
    createdDate: f.createdDate ?? null,
    modifiedDate: f.modifiedDate ?? null,
    creator: f.creator ?? null,
    emailFrom: f.emailFrom ?? null,
    emailTo: joinAddrs(f.emailTo),
    emailCc: joinAddrs(f.emailCc),
    emailBcc: joinAddrs(f.emailBcc),
    emailConversationTopic: f.emailConversationTopic ?? null,
    directPdfThumbnailPath: f.directPdfThumbnailPath ?? null,
    lastBegBates: f.lastBegBates ?? null,
    lastEndBates: f.lastEndBates ?? null,
    lastBegAttachBates: f.lastBegAttachBates ?? null,
    lastEndAttachBates: f.lastEndAttachBates ?? null,
    isContainer: f.isContainer ?? null,
    isSourceStorage: f.isSourceStorage ?? null,
    isTopParent: f.isTopParentResource ?? null,
    hasNotes: f.hasNotes ?? null,
    hasPrivilegedNotes: f.hasPrivilegedNotes ?? null,
    hadProcessingException: f.hadProcessingException ?? null,
    chatConversationId: f.chatConversationId ?? null,
    chatMessageId: f.chatMessageId ?? null,
    chatMessage: f.chatMessage ?? null,
    sortKey: f.sortKey ?? null,
  };
}

// Shared ES envelope → ResourcePage. Total lives at hits.total; page-count/size at
// the top level (see the search response sample).
function mapSearchEnvelope(res: any): ResourcePage {
  const hits = res?.hits ?? {};
  return {
    rows: (hits.hits ?? []).map((h: any) => normalizeSearchHit(h)),
    totalRows: hits.total ?? 0,
    totalFileSize: res?.totalFileSize ?? 0,
    totalPageCount: res?.totalPageCount ?? 0,
  };
}

interface BasicSearchOptions {
  projectId: string;
  /** Omit for a project-wide search; set for a collection-scoped search. */
  collectionId?: string;
  /** The basic-search query string (search.expression in Angular). */
  query: string;
}

/**
 * Basic-search datasource. Hits `POST /project/:id[/collection/:cid]/search`, where
 * the query is a URL param and the body is the column-filter expression. Search
 * endpoints are 1-based, so page0 is converted. Mirrors BasicSearchService.Search.
 */
export function createSearchDatasource(opts: BasicSearchOptions): ResourceDatasource {
  const base = opts.collectionId
    ? `/project/${opts.projectId}/collection/${opts.collectionId}/search`
    : `/project/${opts.projectId}/search`;
  return {
    async fetchPage(req) {
      const qs = buildQueryString({
        page: req.page0 + 1,
        limit: req.limit,
        query: opts.query ?? '',
        sortBy: req.sortBy,
        sortDirection: req.sortDirection,
      });
      // The search input paints itself red off these (Angular BasicSearchService did the
      // same off its promise) — an unparseable query is reported by the server.
      try {
        const res = await api.post<any>(`${base}?${qs}`, req.columnFilters ?? EMPTY_EXPRESSION);
        broadcastSearchResult(true);
        return mapSearchEnvelope(res);
      } catch (e) {
        broadcastSearchResult(false);
        throw e;
      }
    },
  };
}

interface AdvancedSearchOptions {
  projectId: string;
  collectionId?: string;
  /** AdvancedSearch expression object, sent as the POST body. */
  expression: unknown;
}

/**
 * Advanced-search datasource. Hits `.../search/advanced/execute` with the expression
 * object as the POST body (no `query` param). Wired for M3's advanced-search UI;
 * shares the same ES envelope mapping as basic search.
 */
export function createAdvancedSearchDatasource(opts: AdvancedSearchOptions): ResourceDatasource {
  const base = opts.collectionId
    ? `/project/${opts.projectId}/collection/${opts.collectionId}/search/advanced/execute`
    : `/project/${opts.projectId}/search/advanced/execute`;
  return {
    async fetchPage(req) {
      const qs = buildQueryString({
        page: req.page0 + 1,
        limit: req.limit,
        sortBy: req.sortBy,
        sortDirection: req.sortDirection,
      });
      // AND the grid's column filters onto the search, as Angular's SearchService did.
      const body = req.columnFilters
        ? { operator: 0, fields: [], searchExpressions: [opts.expression, req.columnFilters] }
        : opts.expression;
      const res = await api.post<any>(`${base}?${qs}`, body);
      return mapSearchEnvelope(res);
    },
  };
}
